import React, { useEffect, useState, forwardRef, MutableRefObject } from 'react';
import parse from 'html-react-parser';
import { Button } from '@agissc/ktag-map';
import { useTranslation } from 'react-i18next';
import useAppState from '../../../AppStore';
import { useRemainingRequests } from '../api/useLandowners';
import IdentifyMessageBox from './IdentifyMessageBox';

interface LandownerRequestLoggedInButtonButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isLoggedIn: boolean;
  title: string;
  buttonText: string;
  extendedTitle: boolean;
}

const LandownerRequestLoggedInButton = ({
  title,
  buttonText,
  onClick,
  disabled,
  isLoggedIn,
  extendedTitle,
}: LandownerRequestLoggedInButtonButtonProps) => {
  const { t } = useTranslation('agisviewer');

  const remainingRequests = useRemainingRequests().data || 0;
  const remainingAsText = remainingRequests > 10 ? '10+' : `${remainingRequests}/10`;

  return (
    <Button
      onClick={onClick}
      disabled={!isLoggedIn || disabled}
      title={title}
      additionalClassNames="button__max-width button__with-border button__secondary"
    >
      {parse(
        extendedTitle
          ? t('landowner_label_extended', { label: remainingAsText })
          : buttonText,
      )}
    </Button>
  );
};

interface LandownerRequestButtonProps {
  onClick: () => void;
  disabled?: boolean;
}

const getLandownerButtonTitle = (isLoggedIn: boolean, isDisabled: boolean) => {
  if (!isLoggedIn) {
    return 'identify_logIn_message';
  }
  if (isLoggedIn && isDisabled) {
    return 'identify_notInAG_loggedIn_message';
  }
  return '';
};

const LandownerRequestButton = forwardRef<HTMLDivElement, LandownerRequestButtonProps>(
  ({ onClick, disabled = false }: LandownerRequestButtonProps, parentRef) => {
    const { t } = useTranslation('agisviewer');
    const isLoggedIn = useAppState((state) => state.isLoggedIn);

    const [buttonText, setButtonText] = useState('');
    const [buttonTitle, setButtonTitle] = useState('');
    const [extendedTitle, setExtendedTitle] = useState<boolean>(true);

    useEffect(() => {
      const parentElement = (parentRef as MutableRefObject<HTMLDivElement>).current;
      if (!parentElement) return () => {};

      const resizeObserver = new ResizeObserver(() => {
        const parentWidth = parentElement.offsetWidth;
        setButtonTitle(getLandownerButtonTitle(isLoggedIn, disabled));
        if (parentWidth < 300) {
          setButtonText(t('landowner_label'));
          setExtendedTitle(false);
        } else {
          setButtonText(t('landowner_label_extended', { label: '' }));
          setExtendedTitle(true);
        }
      });

      resizeObserver.observe(parentElement);

      return () => resizeObserver.unobserve(parentElement);
    }, [parentRef, disabled]);

    return isLoggedIn ? (
      <LandownerRequestLoggedInButton
        isLoggedIn={isLoggedIn}
        onClick={onClick}
        disabled={!isLoggedIn || disabled}
        title={t(buttonTitle)}
        buttonText={buttonText}
        extendedTitle={extendedTitle}
      />
    ) : (
      <>
        <Button
          onClick={onClick}
          disabled={!isLoggedIn || disabled}
          additionalClassNames="button__max-width button__with-border button__secondary"
        >
          {parse(buttonText)}
        </Button>
        {!isLoggedIn && (
          <IdentifyMessageBox message={t(buttonTitle)} type="information" />
        )}
      </>
    );
  },
);

export default LandownerRequestButton;
